import axios, { AxiosError } from "axios";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { getFieldErrors } from "../../core/api/response-handler";

/**
 * Result type for backend down check
 */
export interface BackendDownResult {
    isBackendDown: boolean;
    message: string;
}

/**
 * Checks if an error indicates the backend is down (no response headers)
 * @param error - The error to check
 * @returns Object with isBackendDown flag and user-friendly message
 */
export function handleBackendDown(error: unknown): BackendDownResult {
    const response: BackendDownResult = { isBackendDown: false, message: "" };

    if (error && axios.isAxiosError(error)) {
        if (!error?.response?.headers) {
            response.isBackendDown = true;
            response.message = "Pentru moment intampinam dificultati tehnice. Vom reveni in cel mai scurt timp!";
        }
    }

    return response;
}

/**
 * Custom error type for handled axios errors
 */
interface CustomAxiosError {
    response: {
        data: {
            message: string;
        };
    };
}

/**
 * Handles axios errors with user-friendly messages
 * @param error - The axios error to handle
 * @returns Rejected promise with formatted error
 */
export const handleAxiosError = (error: AxiosError): Promise<CustomAxiosError> => {
    if (error.message === "Network Error") {
        const networkError: CustomAxiosError = {
            response: {
                data: {
                    message: "Eroare de rețea: Vă rugăm să verificați conexiunea la internet."
                }
            },
        };
        return Promise.reject(networkError);
    }

    if (error.response && error.response.status === 500) {
        const serverError: CustomAxiosError = {
            response: {
                data: {
                    message: "Momentan facem un upgrade al sistemului. Va rugam reincercati in maxim 3 minute.",
                },
            },
        };
        return Promise.reject(serverError);
    }

    return Promise.reject(error);
};

/**
 * Pune erorile pe campuri venite de la backend in formularul react-hook-form.
 *
 * De ce exista: tiparul asta era copiat identic in ~12 hook-uri de formular, fiecare cu
 * propriul `error: any` si `path as any`. Contractul e definit o singura data in
 * ApiValidationError (core/api/response-handler.ts); aici doar il conectam la RHF, cu
 * un singur cast in loc de douasprezece.
 *
 * @returns true daca backendul a trimis erori pe campuri (deci formularul le afiseaza deja)
 */
export function applyServerFieldErrors<T extends FieldValues>(
    error: unknown,
    setError: UseFormSetError<T>
): boolean {
    const fieldErrors = getFieldErrors(error);
    if (!fieldErrors) return false;

    for (const [path, message] of Object.entries(fieldErrors)) {
        setError(path as Path<T>, { type: "server", message });
    }
    return true;
}
